/**
 * i18n configuration for the application
 * Configures react-i18next with language detection and fallback
 *
 * Requirements:
 * - 1.2: Detect user's language preference from browser storage
 * - 7.2: Read language preference from localStorage on initialization
 * - 7.4: Handle localStorage unavailable errors gracefully
 * - 7.5: Fallback to English if all detection fails
 * - 8.3: Configure i18next with language detection plugin
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import type { Resources } from './types'
import { languageNames, supportedLanguages } from './types'

// Import translation files
import crilTranslation from '../locales/cril/translation.json'
import enTranslation from '../locales/en/translation.json'
import ruTranslation from '../locales/ru/translation.json'
import uzTranslation from '../locales/uz/translation.json'

/**
 * Translation resources for all supported languages
 */
export const resources: Resources = {
  uz: { translation: uzTranslation },
  en: { translation: enTranslation },
  ru: { translation: ruTranslation },
  cril: { translation: crilTranslation },
}

/**
 * Initialize i18next with configuration
 *
 * Language detection order (Requirement 1.2, 7.2, 7.4, 7.5):
 * 1. localStorage (key: 'i18n_locale') - User's saved preference
 * 2. navigator - Browser language detection
 * 3. fallback to 'en' - Default if all detection fails
 */
i18n
  // Use language detector plugin (Requirement 8.3)
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize with configuration
  .init({
    // Translation resources
    resources,

    // Fallback language when translation is missing (Requirement 7.5)
    // If localStorage read fails or browser language is unsupported, default to English
    fallbackLng: 'uz',

    // Supported languages
    supportedLngs: [...supportedLanguages],

    // Language detection configuration (Requirements 1.2, 7.2, 7.4)
    detection: {
      // Order of detection methods
      // 1. localStorage - Check saved preference first (Requirement 7.2)
      // 2. navigator - Fall back to browser language (Requirement 7.4)
      order: ['localStorage'],

      // Cache user language preference to localStorage
      caches: ['localStorage'],

      // localStorage key for language preference (Requirement 7.1, 7.2)
      lookupLocalStorage: 'i18n_locale',

      // Gracefully handle localStorage errors (Requirement 7.4)
      // If localStorage is unavailable, the detector will skip it and try navigator
      // If both fail, fallbackLng ('en') will be used
    },

    // Interpolation configuration
    interpolation: {
      // React already escapes values, no need to escape again
      escapeValue: false,
    },

    // React-specific configuration
    react: {
      // Don't use Suspense for now (can be enabled later if needed)
      useSuspense: false,
    },

    // Debug mode (only in development)
    debug: import.meta.env.DEV,
  })

// Export language names for use in components
export { languageNames, supportedLanguages }

// Export configured i18n instance
export default i18n
